/**
 * ThemedTable — data table primitives for the Scholarly Modernist design system.
 *
 * Exports:
 *   DataTableWrapper      — card container (white bg, 20px radius, shadow-card)
 *   ThemedTable           — <table> wrapper (layout fixed, full width)
 *   ThemedTh              — <th> with sort indicator (terracotta ↑↓)
 *   DataTableFooter       — pagination row (info text + pagination controls)
 *   DataTablePagination   — standalone pagination bar
 *   DataTableActionButton — 30×30 icon-only action button (view / edit / delete)
 *
 * Usage pattern:
 *   <DataTableWrapper>
 *     <ThemedTable>
 *       <thead><tr><ThemedTh sorted="asc">Title</ThemedTh>…</tr></thead>
 *       <tbody>…</tbody>
 *     </ThemedTable>
 *     <DataTableFooter total={34} page={1} pageSize={10} totalPages={4} onPageChange={…}/>
 *   </DataTableWrapper>
 *
 * All colors via CSS variables — dark mode automatic.
 * RTL-aware via CSS logical properties.
 *
 * @layer-1 — shared across all features
 */
import { forwardRef } from "react";
import { cn } from "@/shared/lib/utils";
import { useLocale } from "@/shared/hooks/useLocale";
import type { ComponentPropsWithoutRef } from "react";

/* ── DataTableWrapper ──────────────────────────────────────────────────── */

/**
 * Card container that wraps a table.
 * Design spec: white bg, 1px border-light, 20px radius, shadow-card, overflow-hidden.
 */
export function DataTableWrapper({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-[20px]",
        "bg-[var(--card)]",
        "border border-[var(--border-light)]",
        "shadow-[var(--shadow-card)]",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/* ── ThemedTable ───────────────────────────────────────────────────────── */

/**
 * Full-width table with fixed layout and divider row borders.
 * Use inside DataTableWrapper.
 */
export const ThemedTable = forwardRef<HTMLTableElement, ComponentPropsWithoutRef<"table">>(
  function ThemedTable({ className, children, ...props }, ref) {
    return (
      <table
        ref={ref}
        className={cn("w-full border-collapse table-fixed", className)}
        {...props}
      >
        {children}
      </table>
    );
  }
);

/* ── ThemedTh ──────────────────────────────────────────────────────────── */

interface ThemedThProps extends ComponentPropsWithoutRef<"th"> {
  children: React.ReactNode;
  /** Current sort direction for this column. Null/undefined = unsorted. */
  sorted?: "asc" | "desc" | null;
  /** Whether this column is sortable (adds cursor-pointer + idle ⇅ indicator). */
  sortable?: boolean;
  /** Show drag-to-resize handle on the trailing edge. Hidden on last column. */
  resizable?: boolean;
  /** Highlight the resizer handle while dragging. */
  isDraggingResizer?: boolean;
  /** Called when the user presses down on the resize handle. */
  onResizeStart?: (e: React.PointerEvent<HTMLSpanElement>) => void;
}

/**
 * Table header cell with optional sort indicators and column resize handle.
 * Design spec: 10px/700/uppercase/0.10em tracking, terracotta sort arrow.
 * Idle sortable columns show faint ⇅ to signal the affordance without noise.
 */
export function ThemedTh({
  className,
  children,
  sorted,
  sortable,
  resizable,
  isDraggingResizer,
  onResizeStart,
  onClick,
  ...props
}: ThemedThProps) {
  return (
    <th
      className={cn(
        "relative px-5 py-[16px] text-start",
        "text-[10px] font-bold uppercase tracking-[0.10em]",
        "text-[var(--text-tertiary)] whitespace-nowrap",
        "select-none border-b border-[var(--divider)]",
        sortable && "cursor-pointer hover:text-[var(--text-secondary)]",
        "first:ps-7 last:pe-7",
        className
      )}
      onClick={onClick}
      {...props}
    >
      {/* Headers always show in full (whitespace-nowrap, never truncated). Each
          column's minWidth must be wide enough to fit its header; when the sum
          of minWidths exceeds the container the table scrolls horizontally. */}
      {children}
      {/* Idle indicator — faint ⇅ tells users the column is sortable */}
      {sortable && !sorted && (
        <span className="ms-1 text-[9px] text-[var(--text-tertiary)] opacity-35">⇅</span>
      )}
      {sorted === "asc" && (
        <span className="ms-1 text-[var(--terracotta)] text-[9px]">↑</span>
      )}
      {sorted === "desc" && (
        <span className="ms-1 text-[var(--terracotta)] text-[9px]">↓</span>
      )}
      {resizable && onResizeStart && (
        <ColResizer onPointerDown={onResizeStart} isDragging={isDraggingResizer} />
      )}
    </th>
  );
}

/* ColResizer — drag handle on the trailing edge of a header cell.
   Always-visible 1px seam promotes to 2px terracotta on hover/drag. */
function ColResizer({
  onPointerDown,
  isDragging,
}: {
  onPointerDown: (e: React.PointerEvent<HTMLSpanElement>) => void;
  isDragging?: boolean;
}) {
  return (
    <span
      className="group absolute bottom-2 top-2 z-[2] flex w-[7px] cursor-col-resize items-center justify-center"
      style={{ right: -3 }}
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        className={cn(
          "h-full transition-all duration-[120ms]",
          isDragging
            ? "w-0.5 bg-[var(--terracotta)] opacity-100"
            : "w-px bg-[var(--border-light)] opacity-70 group-hover:w-0.5 group-hover:bg-[var(--terracotta)] group-hover:opacity-100"
        )}
      />
    </span>
  );
}

/* ── ThemedTd ──────────────────────────────────────────────────────────── */

/**
 * Table body cell — standard 13.5px body text, 16px vertical padding.
 */
export function ThemedTd({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"td">) {
  return (
    <td
      className={cn(
        "overflow-hidden px-5 py-[16px] align-middle text-[13.5px]",
        "first:ps-7 last:pe-7",
        className
      )}
      {...props}
    >
      {children}
    </td>
  );
}

/* ── ThemedTr ──────────────────────────────────────────────────────────── */

/**
 * Table body row with hover fill and divider bottom border.
 */
export function ThemedTr({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"tr">) {
  return (
    <tr
      className={cn(
        "border-b border-[var(--divider)] last:border-b-0",
        "cursor-pointer transition-[transform,background-color] duration-100",
        "hover:bg-[var(--surface-hover)]",
        "active:scale-[0.997] active:bg-[var(--surface-hover)]",
        "motion-reduce:active:scale-100",
        className
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

/* ── DataTableActionButton ─────────────────────────────────────────────── */

export type DataTableActionVariant = "default" | "danger" | "success" | "warning";

interface DataTableActionButtonProps
  extends ComponentPropsWithoutRef<"button"> {
  /** Controls the hover color scheme. */
  variant?: DataTableActionVariant;
}

/**
 * 30×30 icon-only action button for table rows.
 * Default state: transparent bg, muted border, tertiary icon.
 * Hover state: semantic fill per variant (neutral / error-red / success-green / warning-amber).
 * Design spec: 30×30, 8px radius, border-mid, transparent bg.
 */
export function DataTableActionButton({
  variant = "default",
  className,
  children,
  ...props
}: DataTableActionButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "flex size-[30px] shrink-0 items-center justify-center",
        "rounded-[8px] border border-[var(--border-mid)] bg-transparent",
        "text-[var(--text-tertiary)] transition-all duration-[120ms]",
        "cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-gold)]",
        variant === "default" && [
          "hover:bg-[var(--surface-low)]",
          "hover:text-[var(--text-primary)]",
          "hover:border-[var(--border-muted)]",
        ],
        variant === "danger" && [
          "hover:bg-[var(--action-btn-reject-bg)]",
          "hover:text-[var(--action-btn-reject-text)]",
          "hover:border-[var(--action-btn-reject-border)]",
        ],
        variant === "success" && [
          "hover:bg-[var(--action-btn-approve-bg)]",
          "hover:text-[var(--action-btn-approve-text)]",
          "hover:border-[var(--action-btn-approve-border)]",
        ],
        variant === "warning" && [
          "hover:bg-[var(--action-btn-suspend-bg)]",
          "hover:text-[var(--action-btn-suspend-text)]",
          "hover:border-[var(--action-btn-suspend-border)]",
        ],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/* ── DataTableFooter ───────────────────────────────────────────────────── */

interface DataTableFooterProps {
  /** Human-readable info string — e.g. "5 of 34 titles". */
  info?: string;
  /** Pagination props passed through to DataTablePagination. */
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

/**
 * Table footer row with info text (start) and pagination (end).
 * Design spec: surface-low bg, 14px 28px padding, divider top border.
 */
export function DataTableFooter({
  info,
  page,
  totalPages,
  onPageChange,
  className,
}: DataTableFooterProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between",
        "px-7 py-[14px]",
        "border-t border-[var(--divider)]",
        "bg-[var(--draft-bg)]",
        className
      )}
    >
      {info && (
        <span className="text-[11.5px] text-[var(--text-tertiary)]">
          {info}
        </span>
      )}
      <DataTablePagination
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </div>
  );
}

/* ── DataTablePagination ───────────────────────────────────────────────── */

interface DataTablePaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

/**
 * Pagination control bar.
 * Design spec: 28×28 page buttons (7px radius), active = ink-filled,
 * prev/next arrow SVG buttons.
 */
export function DataTablePagination({
  page,
  totalPages,
  onPageChange,
  className,
}: DataTablePaginationProps) {
  const { fmtNum } = useLocale();
  if (totalPages <= 1) return null;

  /** Build visible page numbers with ellipsis. */
  function buildPages(): (number | "…")[] {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | "…")[] = [1];
    if (page > 3) pages.push("…");
    for (
      let i = Math.max(2, page - 1);
      i <= Math.min(totalPages - 1, page + 1);
      i++
    ) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push("…");
    pages.push(totalPages);
    return pages;
  }

  const pages = buildPages();

  return (
    <div className={cn("flex items-center gap-[3px]", className)}>
      {/* Prev */}
      <PageButton
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label="Previous page"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </PageButton>

      {/* Page numbers */}
      {pages.map((p, i) =>
        p === "…" ? (
          <span
            key={`sep-${i}`}
            className="px-0.5 text-[11px] text-[var(--text-tertiary)] opacity-50"
          >
            …
          </span>
        ) : (
          <PageButton
            key={p}
            active={p === page}
            onClick={() => onPageChange(p)}
          >
            {fmtNum(p)}
          </PageButton>
        )
      )}

      {/* Next */}
      <PageButton
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        aria-label="Next page"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </PageButton>
    </div>
  );
}

/* Page button helper (internal) */
function PageButton({
  active,
  disabled,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<"button"> & { active?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        "flex size-7 items-center justify-center rounded-[7px]",
        "border font-semibold text-[12px] font-sans",
        "transition-colors duration-100",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-gold)]",
        active
          ? "border-[var(--btn-primary-bg)] bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)]"
          : "border-transparent bg-transparent text-[var(--text-tertiary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]",
        disabled && "opacity-40 pointer-events-none",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}
